import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/Controlled";

/**
 * ReasonModal — Phase A Run 2 sensitive-action reason capture.
 *
 * Mirrors the existing Attorney Review dialog pattern in TrackEditor.jsx
 * for UI consistency. Enforced rules:
 *  * Category is required (SearchableSelect).
 *  * Detail is required, minimum 10 characters after trim.
 *  * Confirm button is disabled until both fields validate.
 *  * On confirm, emits { category, detail }. The parent joins them into
 *    the backend `category` / `detail` fields on the ReasonedActionReq.
 *
 * Props:
 *   open, onOpenChange     — dialog visibility control
 *   title                  — dialog title (e.g. "Withdraw Title")
 *   description            — one-line explanation shown under the title
 *   categoryOptions        — string[] of category values to offer
 *   confirmLabel           — text on the confirm button
 *   busy                   — bool; disables inputs while server processes
 *   severity               — "info" | "low" | "high" | "critical" (visual only)
 *   onConfirm(payload)     — async callback with { category, detail }
 *   testid                 — root data-testid prefix (default "reason-modal")
 */
export const ReasonModal = ({
  open,
  onOpenChange,
  title,
  description,
  categoryOptions = [],
  confirmLabel = "Confirm",
  busy = false,
  severity = "high",
  onConfirm,
  testid = "reason-modal",
  minDetailLength = 10,
}) => {
  const [category, setCategory] = useState("");
  const [detail, setDetail] = useState("");

  // Reset when dialog re-opens.
  useEffect(() => {
    if (open) {
      setCategory("");
      setDetail("");
    }
  }, [open]);

  const detailTrim = detail.trim();
  const remaining = Math.max(0, minDetailLength - detailTrim.length);
  const isValid = useMemo(
    () => Boolean(category && detailTrim.length >= minDetailLength),
    [category, detailTrim, minDetailLength],
  );

  const severityAccent = severity === "critical"
    ? "hsl(0 70% 42%)"
    : severity === "high"
      ? "hsl(24 74% 46%)"
      : "hsl(215 30% 40%)";

  const handleConfirm = async () => {
    if (!isValid || busy) return;
    try {
      await onConfirm({ category, detail: detailTrim });
    } catch (_e) {
      // parent handles toasts; keep the modal open on error
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="mt-1">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Category</Label>
          <SearchableSelect
            value={category}
            onChange={setCategory}
            options={categoryOptions}
            placeholder="Select a reason\u2026"
            testid={`${testid}-category`}
          />
        </div>

        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Detail (min {minDetailLength} characters)
          </Label>
          <Textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={4}
            placeholder="Explain the reason so the audit trail is meaningful\u2026"
            className="mt-1"
            data-testid={`${testid}-detail`}
            disabled={busy}
          />
          <div className="flex items-center justify-between mt-1 text-[11px] text-muted-foreground">
            <span>
              {detailTrim.length} / {minDetailLength}+ characters
            </span>
            {remaining > 0 ? (
              <span className="text-amber-800/80">Need {remaining} more character{remaining === 1 ? "" : "s"}.</span>
            ) : (
              <span className="text-emerald-800/80">Ready to save.</span>
            )}
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || busy}
            style={{ background: severityAccent, color: "white" }}
            data-testid={`${testid}-confirm`}
          >
            {busy ? "Saving\u2026" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReasonModal;
